import React, {useMemo} from 'react';
import {classes, style} from './doppe-action-type-articles-list-widget.st.css';
import {listViewCreateDataSource, useObservableValue} from '@wix/devzai-utils-react';
import {DoppeActionTypeArticlesList} from '../doppe-action-type-articles-list';
import {DoppeActionGoalType, DoppeActionType} from '../../../client-server-common/doppe-action-types/doppe-action-type';
import {
    DoppeActionTypeWidgetItemList
} from '../../doppe-actions-common/doppe-action-type-widget-item-list/doppe-action-type-widget-item-list';
import {
    DoppeActionTypeCommonListViewItem
} from '../../doppe-actions-common/doppe-action-type-list-view-item/doppe-action-type-common-list-view-item/doppe-action-type-common-list-view-item';
import {
    DoppeAutoBlogPostType,
    DoppeBlogPostsExternalDataSourceType,
    DoppeBusinessEntity,
    doppeBusinessEntityDtoIsAvailable,
    doppeExternalBlogPostProviderResolveBusinessEntityReference,
    doppeHideableValueGetVisibleValue,
    doppeHideableValueIsVisibleAndNotEqualValue,
    doppeHideableValueToggleHidden,
    doppeListActionListItemCalculateItemButtons,
    doppeListActionListItemCalculateItemSections,
    doppeListActionSettingsFilterHiddenListItemsFromActionView
} from '../../../client-server-common';
import {
    DoppeActionTypeBlogPostBindableWidget
} from '../../doppe-action-type-blog-post/doppe-action-type-blog-post-widget/doppe-action-type-blog-post-widget';
import {bymoPageContextPropsResolveBusinessEntity, useBymoPageContextProps} from '../../../doppe-sdk';
import {arraySkipTake, evaluateFunction, iterableMapToArray} from '@wix/devzai-utils-common';
import {
    DoppeActionUnavailablePreview,
    DoppeActionUnavailableReason
} from '../../../components/doppe-action-unavailable-preview/doppe-action-unavailable-preview';
import {
    DoppeActionTypeWidgetLayout
} from '../../../components/doppe-action-type-widget-layout/doppe-action-type-widget-layout';
import {
    doppeViewerActionHandleListItemClick
} from '../../../doppe-sdk/doppe-viewer-action-context/doppe-viewer-action-context-utils';
import {
    useAssertWidgetRenderingContext
} from '../../../components/doppe-viewer-widget-rendering-context/doppe-viewer-widget-rendering-context';
import {
    doppeBlogPostsListActionResolveExternalItemSettingsFromDoppeDtoBlogPost
} from "../../../doppe-action-types-client-server-common/doppe-blog-post-action-types-utils/doppe-blog-post-action-types-utils";


export const DoppeActionTypeArticlesListWidget = React.memo(function DoppeActionTypeArticlesListWidget(
    props: DoppeActionType.WidgetProps<DoppeActionTypeArticlesList.ActionSettings>
) {
    const {
        actionContext,

        className,
        ...htmlAttributes
    } = props;

    const {
        observableCurrentItemIdToRender,
    } = useAssertWidgetRenderingContext();

    const actionSettings = actionContext.action.settings;

    const {
        listDataItems,
        layout,

        externalDataSource,
        externalDataSourceItemsCount,

        previewShowBlogPostDate,
        previewShowBlogWebsite,
        previewShowDescription,
        previewShowTitle,

        itemShowDescription,
        itemShowImage,
        itemShowTitle,
        itemClickBehaviour
    } = actionSettings;

    const bymoPageContextProps = useBymoPageContextProps();

    const displayedArticles = useMemo(() => {
        if (externalDataSource === null) {
            return listDataItems;
        } else {

            const externalArticles = evaluateFunction(() => {
                switch (externalDataSource.type) {
                    case DoppeBlogPostsExternalDataSourceType.LatestWixBlogPosts: {
                        return bymoPageContextPropsResolveBusinessEntity(bymoPageContextProps, {
                            businessEntity: DoppeBusinessEntity.WixAutoBlogPostsList,
                            id: DoppeAutoBlogPostType.Latest
                        }) ?? []
                    }
                }
            })

            return arraySkipTake(externalArticles, 0, externalDataSourceItemsCount).map(doppeDtoBlogPost => {
                return {
                    ...doppeBlogPostsListActionResolveExternalItemSettingsFromDoppeDtoBlogPost({
                        blogPost: doppeDtoBlogPost,
                        listItemClickBehaviour: itemClickBehaviour
                    }),
                    // We override the provider and the id in the provider since the values that are fetched
                    // here are already synced with the external provider.
                    blogPostProvider: null,
                    blogPostProviderId: null,
                } satisfies DoppeActionTypeArticlesList.ArticleSettings
            });
        }
    }, [externalDataSource, externalDataSourceItemsCount, listDataItems, bymoPageContextProps])

    const expandedBlogPostId = useObservableValue(observableCurrentItemIdToRender);


    const currentBlogPostToShow = useMemo(
        () => displayedArticles.find(product => product.id === expandedBlogPostId) ?? null,
        [displayedArticles, expandedBlogPostId]);


    const availableItems = useMemo(() => {
        return iterableMapToArray(doppeListActionSettingsFilterHiddenListItemsFromActionView(displayedArticles), (listItem, skip) => {

            const {
                blogPostProviderId,
                blogPostProvider
            } = listItem;

            if (blogPostProvider !== null && blogPostProviderId !== null) {
                const boundBusinessEntity = bymoPageContextPropsResolveBusinessEntity(
                    bymoPageContextProps,
                    doppeExternalBlogPostProviderResolveBusinessEntityReference(blogPostProvider, blogPostProviderId)
                );

                if (doppeBusinessEntityDtoIsAvailable(boundBusinessEntity)) {
                    return {
                        ...listItem,
                        businessEntityDto: boundBusinessEntity
                    }
                } else {
                    return skip;
                }
            } else {
                return {
                    ...listItem,
                    businessEntityDto: undefined
                }
            }
        })
    }, [displayedArticles, bymoPageContextProps]);

    const listViewDataSource = useMemo(() => {
        return listViewCreateDataSource(availableItems, {
            idFunction: item => item.id
        });
    }, [availableItems]);

    return (
        <DoppeActionTypeWidgetLayout
            {...htmlAttributes}
            className={style(classes.root, {}, className)}
            actionContext={actionContext}
        >
            {evaluateFunction(() => {
                if (!currentBlogPostToShow) {
                    if (listViewDataSource.length === 0) {
                        return (
                            <DoppeActionUnavailablePreview
                                reason={DoppeActionUnavailableReason.ContentUnavailable}
                            />
                        )
                    } else {
                        return (
                            <DoppeActionTypeWidgetItemList
                                className={style(classes.listContent)}
                                layout={layout}
                                listTitle={actionSettings.mainTitle}
                                media={doppeHideableValueGetVisibleValue(actionSettings.mainImage, undefined)}
                                mediaViewSettings={actionSettings.mainMediaViewSettings}
                                mediaViewLayout={actionSettings.mainMediaViewLayout}
                                onItemClick={eventData => {
                                    doppeViewerActionHandleListItemClick(actionContext, {
                                        itemId: eventData.id,
                                        itemIndex: availableItems.findIndex(item => item.id === eventData.id),
                                        itemLink: eventData.blogPostLink,
                                    })
                                }}
                                actionContext={actionContext}
                                renderItem={(dataItem, layout, index) => {
                                    const {
                                        blogPostImages,
                                        blogPostTitle,
                                        description
                                    } = dataItem;

                                    return (
                                        <DoppeActionTypeCommonListViewItem
                                            actionContext={actionContext}
                                            innerItemId={dataItem.id}
                                            innerItemIndex={index}
                                            title={blogPostTitle}
                                            description={doppeHideableValueIsVisibleAndNotEqualValue(description, '') ? description : ''}
                                            image={blogPostImages?.[0] ?? null}
                                            layout={layout}
                                            isOneItem={listViewDataSource.length === 1}
                                            hideImageWhenPossible={!itemShowImage}
                                            hideTitleWhenPossible={!itemShowTitle}
                                            hideDescriptionWhenPossible={!itemShowDescription}
                                        />
                                    )
                                }}
                                dataSourceItems={listViewDataSource}
                            />

                        )
                    }
                } else {
                    return (
                        <DoppeActionTypeBlogPostBindableWidget
                            actionContext={actionContext}
                            listItemId={currentBlogPostToShow.id}
                            reportGoalAchieved={(goalType: DoppeActionGoalType) => {
                                actionContext.reportGoalAchieved({
                                    goalType: goalType,
                                    innerItemId: currentBlogPostToShow.id,
                                    innerItemPosition: displayedArticles.findIndex(item => item.id === expandedBlogPostId)
                                });
                            }}
                            actionId={actionContext.action.id}
                            actionSettings={{
                                blogPostImages: currentBlogPostToShow.blogPostImages,
                                showBlogWebsite: previewShowBlogWebsite && currentBlogPostToShow.showBlogWebsite,
                                description: doppeHideableValueToggleHidden(
                                    currentBlogPostToShow.description,
                                    isHidden => isHidden ? true : !previewShowDescription
                                ),
                                blogPostTitle: doppeHideableValueToggleHidden(
                                    currentBlogPostToShow.blogPostTitle,
                                    isHidden => isHidden ? true : !previewShowTitle
                                ),
                                blogPostDate: doppeHideableValueToggleHidden(
                                    currentBlogPostToShow.blogPostDate,
                                    isHidden => isHidden ? true : !previewShowBlogPostDate
                                ),
                                blogPostLink: currentBlogPostToShow.blogPostLink,
                                blogPostProvider: currentBlogPostToShow.blogPostProvider,
                                blogPostProviderId: currentBlogPostToShow.blogPostProviderId,
                                mainMediaViewSettings: currentBlogPostToShow.mainMediaViewSettings,
                                mainMediaViewLayout: currentBlogPostToShow.mainMediaViewLayout,
                                ...doppeListActionListItemCalculateItemButtons(actionSettings.listItemTemplateCtaButtonsSettings, currentBlogPostToShow),
                                ...doppeListActionListItemCalculateItemSections(actionSettings.listItemTemplateSectionsSettings, currentBlogPostToShow),
                            }}
                        />
                    )
                }
            })}
        </DoppeActionTypeWidgetLayout>
    );
});


